import React from 'react';
import { Box, Button, Divider, Drawer, IconButton, InputLabel, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { AMENDMENT_STATUSES, AMENDMENT_TYPES } from '../../../../model/sge/amendment/amendment-enums';
import { IAmendmentPagedRequest } from '../../../../model/sge/amendment/request/amendment-paged-request';
import { IParlamentarianPagedResponse } from '../../../../model/sge/parlamentarian/response/parlamentarian-paged-response';
import { IPoliticalPartyPagedResponse } from '../../../../model/sge/political-party/response/political-party-paged-response';
import { IBudgetUnitResponse } from '../../../../model/sge/budget-unit/response/budget-unit-response';
import { IBudgetProgramResponse } from '../../../../model/sge/budget-program/response/budget-program-response';
import { IBudgetActionResponse } from '../../../../model/sge/budget-action/response/budget-action-response';
import { formatCodeName } from '../../components/code-name-label';

interface AmendmentMoreFiltersDrawerProps {
    open: boolean;
    parameters: IAmendmentPagedRequest;
    parlamentarians: IParlamentarianPagedResponse[];
    politicalParties: IPoliticalPartyPagedResponse[];
    budgetUnits: IBudgetUnitResponse[];
    budgetPrograms: IBudgetProgramResponse[];
    budgetActions: IBudgetActionResponse[];
    onChange: (e: any) => void;
    onClear: () => void;
    onSearch: () => void;
    onClose: () => void;
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box>
            <InputLabel>{label}</InputLabel>
            {children}
        </Box>
    );
}

// Filtros avançados da listagem de emendas, em painel lateral para não poluir a busca principal
export function AmendmentMoreFiltersDrawer({
    open, parameters, parlamentarians, politicalParties, budgetUnits, budgetPrograms, budgetActions, onChange, onClear, onSearch, onClose,
}: AmendmentMoreFiltersDrawerProps) {
    return (
        <Drawer anchor="right" open={open} onClose={onClose} sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}>
            <Box sx={{ width: { xs: '100vw', sm: 360 }, p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterListIcon color="action" />
                        <Typography variant="h6">Mais filtros</Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} aria-label="Fechar">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Divider />

                <FilterField label="Exercício">
                    <OutlinedInput fullWidth name="input-year" type="number" value={parameters.year ?? ''} onChange={onChange} inputProps={{ min: 2000, max: new Date().getFullYear() + 1 }} />
                </FilterField>
                <FilterField label="Status">
                    <Select fullWidth name="select-status" value={parameters.status ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {AMENDMENT_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                    </Select>
                </FilterField>
                <FilterField label="Tipo">
                    <Select fullWidth name="select-type" value={parameters.type ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {AMENDMENT_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                    </Select>
                </FilterField>
                <FilterField label="Parlamentar">
                    <Select fullWidth name="select-parlamentar" value={parameters.parlamentarId ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {parlamentarians.map((p) => <MenuItem key={p.id} value={p.id}>{p.personName}</MenuItem>)}
                    </Select>
                </FilterField>
                <FilterField label="Partido político">
                    <Select fullWidth name="select-political-party" value={parameters.politicalPartyId ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {politicalParties.map((p) => <MenuItem key={p.id} value={p.id}>{p.abbreviation} - {p.name}</MenuItem>)}
                    </Select>
                </FilterField>
                <FilterField label="Valor mínimo">
                    <OutlinedInput fullWidth name="input-min-value" type="number" value={parameters.minValue ?? ''} onChange={onChange} inputProps={{ min: 0, step: 0.01 }} />
                </FilterField>
                <FilterField label="Valor máximo">
                    <OutlinedInput fullWidth name="input-max-value" type="number" value={parameters.maxValue ?? ''} onChange={onChange} inputProps={{ min: 0, step: 0.01 }} />
                </FilterField>
                <FilterField label="Criada de">
                    <OutlinedInput fullWidth name="input-start-date" type="date" value={parameters.startDate || ''} onChange={onChange} />
                </FilterField>
                <FilterField label="Criada até">
                    <OutlinedInput fullWidth name="input-end-date" type="date" value={parameters.endDate || ''} onChange={onChange} />
                </FilterField>
                <FilterField label="Unidade orçamentária">
                    <Select fullWidth name="select-budget-unit" value={parameters.budgetUnitId ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todas</em></MenuItem>
                        {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{formatCodeName(u)}</MenuItem>)}
                    </Select>
                </FilterField>
                <FilterField label="Programa orçamentário">
                    <Select fullWidth name="select-budget-program" disabled={!parameters.budgetUnitId} value={parameters.budgetProgramId ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todos</em></MenuItem>
                        {budgetPrograms.map((p) => <MenuItem key={p.id} value={p.id}>{formatCodeName(p)}</MenuItem>)}
                    </Select>
                </FilterField>
                <FilterField label="Ação orçamentária">
                    <Select fullWidth name="select-budget-action" disabled={!parameters.budgetProgramId} value={parameters.budgetActionId ?? ''} onChange={onChange}>
                        <MenuItem value=""><em>Todas</em></MenuItem>
                        {budgetActions.map((a) => <MenuItem key={a.id} value={a.id}>{formatCodeName(a)}</MenuItem>)}
                    </Select>
                </FilterField>

                <Box sx={{ mt: 'auto', display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={onClear}>Limpar</Button>
                    <Button variant="contained" startIcon={<SearchIcon />} onClick={onSearch}>Consultar</Button>
                </Box>
            </Box>
        </Drawer>
    );
}
